import { forwardRef, useId, type InputHTMLAttributes, type ReactNode } from 'react';
import { HelpTip } from '../HelpTip';
import { useDeferredError } from './useDeferredError';

interface TextFieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'id'> {
  label: string;
  /**
   * Shown in the label's help icon, never as a line of its own — a description that took a
   * line would push this field's input below its neighbour's. Still reaches screen readers
   * through an `sr-only` node wired to `aria-describedby`.
   */
  description?: string;
  /** Field-level message from the server or client validation. */
  error?: string;
  /** Rendered inside the control on the leading edge — an icon, never text. */
  leading?: ReactNode;
  /** Rendered inside the control on the trailing edge — e.g. a show/hide toggle. */
  trailing?: ReactNode;
  /**
   * How much room the value leaves for `trailing`.
   *
   * The default clears one icon button. A trailing slot holding **more** than that — a divided
   * readout segment beside the button — needs `wide`, or the value runs underneath it: the
   * slot is absolutely positioned, so it does not push the text out of its own way.
   *
   * Deliberately two named steps rather than a free-form class. A caller passing its own
   * padding utility would be competing with this one at equal specificity, and which of the
   * two won would depend on Tailwind's stylesheet order rather than on the call site.
   */
  trailingWidth?: 'default' | 'wide';
  /** Required for a stable, non-index-based DOM id. Falls back to a generated one. */
  id?: string;
  /**
   * Render the control ALONE — no label row, no help icon.
   *
   * **The caller then owes a real `<label htmlFor>`**, and that obligation is the whole of
   * this prop: it does not hide the label, it hands ownership over. For a key/value table,
   * where the label is a cell of the table rather than a line above the box, this is the
   * only way to keep the control shared — the alternative is a bare `<input>`, which is the
   * drift this component exists to prevent.
   *
   * `label` is still required and still used: it becomes the control's `aria-label`, which
   * is what a screen reader announces — so it must match the caller's visible text exactly.
   *
   * **Pass `description` as well when there is one.** This renders no help icon, but it
   * still renders the `sr-only` twin and wires `aria-describedby` to it, which is the half
   * that must not be dropped. The caller draws the visible `<HelpTip>` beside its own label.
   */
  labelHidden?: boolean;
}

/**
 * The shared text input. Never write a bare `<input>` — this owns the label association,
 * the error wiring, and the control's visual baseline, and it is the only place any of that
 * is decided.
 */
export const TextField = forwardRef<HTMLInputElement, TextFieldProps>(function TextField(
  {
    label,
    description,
    error,
    leading,
    trailing,
    trailingWidth = 'default',
    labelHidden = false,
    id,
    className = '',
    required,
    ...rest
  },
  ref,
) {
  const generatedId = useId();
  const fieldId = id ?? generatedId;

  /*
    A required-but-empty field must not open red. `useDeferredError` holds back an error the
    input mounted with until the reader touches it; anything that appears or changes later —
    a value typed out of range, a server rejection — shows immediately.

    It lives here rather than at 46 call sites, which is where it was going wrong: every one
    of them was individually defensible ("the field really is required") and together they
    made every create form in the product open in a failed state.
  */
  const { error: shownError, markTouched } = useDeferredError(error);

  const describedBy = [
    description ? `${fieldId}-desc` : null,
    shownError ? `${fieldId}-err` : null,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className="flex flex-col gap-1.5">
      {/* Labels are regular weight and exactly one line tall, so a row of fields aligns. */}
      {labelHidden ? null : (
        <div className="flex min-h-5 items-center gap-1.5">
          <label htmlFor={fieldId} className="text-sm text-slate-700">
            {label}
            {required ? (
              <span className="ml-0.5 text-red-600" aria-hidden="true">
                *
              </span>
            ) : null}
          </label>

          {description ? <HelpTip label={description} /> : null}
        </div>
      )}

      {description ? (
        <span id={`${fieldId}-desc`} className="sr-only">
          {description}
        </span>
      ) : null}

      <div className="relative">
        {leading ? (
          <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-slate-400">
            {leading}
          </span>
        ) : null}

        <input
          {...rest}
          ref={ref}
          id={fieldId}
          name={rest.name ?? fieldId}
          required={required}
          aria-invalid={shownError ? true : undefined}
          onBlur={(e) => {
            markTouched();
            rest.onBlur?.(e);
          }}
          /*
            The name for a label-less field.

            **`aria-label` WINS over an associated `<label>`, not the other way round** —
            accname resolves aria-labelledby, then aria-label, then the label element. So a
            caller that renders its own `<label htmlFor>` is NOT overriding this: what the
            screen reader announces is still this `label` prop.

            The obligation that follows, and it is the whole reason this is spelled out:
            **the caller's visible text and this `label` prop must be the same string.** Let
            them diverge and the control is announced as something other than what is on
            screen, which is WCAG 2.5.3 failing silently. The caller's `<label>` still earns
            its keep — clicking it focuses the box — it just is not what names the control.
          */
          aria-label={labelHidden ? label : undefined}
          aria-describedby={describedBy || undefined}
          className={[
            'block w-full rounded-lg border bg-white text-sm text-slate-600 transition',
            'min-h-10 px-3 py-2 placeholder:text-slate-400',
            leading ? 'pl-10' : '',
            trailing ? (trailingWidth === 'wide' ? 'pr-24' : 'pr-10') : '',
            shownError
              ? 'border-red-400 focus:border-red-500 focus:ring-2 focus:ring-red-100'
              : 'border-slate-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-100',
            'focus:outline-none disabled:cursor-not-allowed disabled:bg-slate-50 disabled:text-slate-400',
            className,
          ]
            .filter(Boolean)
            .join(' ')}
        />

        {trailing ? (
          <span className="absolute inset-y-0 right-0 flex items-center pr-2">{trailing}</span>
        ) : null}
      </div>

      {/* Errors always sit below the field, never beside or above it. */}
      {shownError ? (
        <p id={`${fieldId}-err`} className="text-xs text-red-600" role="alert">
          {shownError}
        </p>
      ) : null}
    </div>
  );
});
